/**
 * ShogunChop Resource
 *
 * Wraps ShogunChop's localhost HTTP API. Unlike other SDK resources, this
 * uses plain fetch() against a separate base URL (localhost trust model,
 * no auth headers).
 */

import type {
  ShogunChopAuthInfo,
  ShogunChopAuthSnapshot,
  ShogunChopAuthStatus,
  ShogunChopBridgeStatus,
  ShogunChopMcpServer,
  ShogunChopMcpTool,
  AddShogunChopMcpServerRequest,
  ShogunChopIndexStartRequest,
  ShogunChopIndexStatus,
  ShogunChopInfo,
  ShogunChopToolResult,
  ShogunChopEvent,
  ShogunChopAuthChangedData,
  ShogunChopVersion,
  ShogunChopEndpoints,
  ShogunChopRestoredAuth,
} from '../types/shogunchop.js';
import type { WorkspaceProjection } from '../types/workspace-state.js';
import type { WebSocketFactory, WebSocketLike } from '../chat/types.js';
import { ShogunChopHTTPError } from '../errors.js';
import { parseWorkspaceProjection } from '../workspace-projection.js';
import {
  WORKSPACE_CONTEXT_OBSERVED,
  parseWorkspaceContextObserved,
} from '../workspace-context-observed.js';
import {
  WorkspaceProjectionSynchronizer,
  type WorkspaceProjectionHandler,
} from './bridge-events.js';
import type { CatalogRegistrar } from './catalogs.js';

/** An `auth:changed` frame, narrowed out of the daemon's event union. */
export type ShogunChopAuthChangedEvent = Extract<ShogunChopEvent, { event: 'auth:changed' }>;

/** Re-exported for the identity daemon-arm's mirror (sdk ADR-010). */
export type { ShogunChopEvent };

/** What a waiter should do with an `auth:changed` frame it just received. */
export type AuthFrameDecision =
  /** A replay or an out-of-order duplicate: it carries no new information. */
  | { action: 'ignore' }
  /**
   * Fresh. Carries the WHOLE frame payload, not just the phase: the mirror
   * table needs `reason` (on the no_credential arm) and
   * `credential_fingerprint` (the replay-suppression check), and exporting the
   * narrow shape is what kept the app-shell duplicate alive
   * (sdk ADR-010).
   */
  | { action: 'accept'; seq: number; data: ShogunChopAuthChangedData }
  /** The frame violates the /events contract. Surface it; never skip past it. */
  | { action: 'reject'; message: string };

/**
 * The `seq` guard, as a pure decision so it is testable without a socket.
 *
 * `ShogunChopEventStream` auto-reconnects, so a duplicate or out-of-order frame
 * is a NORMAL delivery outcome — the daemon may re-emit its current state on a
 * fresh connection. Without a monotonic gate, a replayed `usable` from before a
 * sign-out would resolve a sign-in that has not happened.
 *
 * A missing or non-finite `seq` is REJECTED rather than tolerated: a guard that
 * silently accepts unguardable frames is not a guard, and the disagreement
 * between daemon and SDK about the wire is exactly what must surface.
 */
export function decideAuthChangedFrame(
  highestSeq: number,
  frame: ShogunChopAuthChangedEvent
): AuthFrameDecision {
  const seq = (frame as { seq?: unknown }).seq;
  if (typeof seq !== 'number' || !Number.isFinite(seq)) {
    return {
      action: 'reject',
      message:
        `auth:changed frame carries no numeric seq (got ${JSON.stringify(seq)}) — ` +
        `the daemon /events contract is broken`,
    };
  }
  if (seq <= highestSeq) return { action: 'ignore' };
  return { action: 'accept', seq, data: frame.data };
}

/**
 * A subscriber's view of the shared `/events` stream. `release()` drops this
 * subscriber's handlers and its reference; there is deliberately no `close()`,
 * because one subscriber closing the socket would deafen all the others.
 */
export interface ShogunChopSharedEvents {
  on(handler: ShogunChopEventHandler): () => void;
  onOpen(handler: ShogunChopOpenHandler): () => void;
  onClose(handler: ShogunChopCloseHandler): () => void;
  registerWorkspaceProjection(
    workspaceId: string,
    snapshot: () => Promise<WorkspaceProjection>,
    subscribe: (afterRevision: number) => Promise<void>,
    unsubscribe: () => Promise<void>,
    handler: WorkspaceProjectionHandler,
    onError: (error: unknown) => void
  ): { registered: Promise<void>; unsubscribe: () => void };
  send(frame: Record<string, unknown>): void;
  requestCatalogHold(op: CatalogHoldOp, scopeType: string, scopeId: string): Promise<void>;
  release(): void;
}

export type CatalogHoldOp = 'catalog.hold' | 'catalog.release';

class EventsSocketClosedError extends Error {}

// A socket's end releases every hold it carried, so a release with no open socket is already done (shogunchop ADR-ab462).
export function createDaemonCatalogRegistrar(events: ShogunChopSharedEvents): CatalogRegistrar {
  return {
    subscribe: (_deviceId, scopeType, scopeId) =>
      events.requestCatalogHold('catalog.hold', scopeType, scopeId),
    unsubscribe: (_deviceId, scopeType, scopeId) =>
      events.requestCatalogHold('catalog.release', scopeType, scopeId).catch((err: unknown) => {
        if (err instanceof EventsSocketClosedError) return;
        throw err;
      }),
  };
}

export class ShogunChopResource {
  private readonly baseUrl: string;
  /** Browser-launch callback for `signIn()`. Required for `signIn()` to
   *  work; the constructor accepts it as the second arg so existing
   *  zero-config callers (e.g. integration tests that only need MCP/index
   *  surfaces) keep compiling. */
  private readonly openExternal?: (url: string) => Promise<void>;
  /** WebSocket factory for `signIn()`'s `auth:changed` subscription.
   *  Required when calling `signIn()` from a runtime without a global
   *  WebSocket (e.g. Node 18+ has one; older Node needs a polyfill).
   *  The renderer-process SDK uses `globalThis.WebSocket` directly via the
   *  default factory. */
  private readonly wsFactory?: WebSocketFactory;
  private sharedStream: ShogunChopEventStream | null = null;
  private sharedRefs = 0;

  constructor(
    baseUrl: string,
    openExternal?: (url: string) => Promise<void>,
    wsFactory?: WebSocketFactory
  ) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.openExternal = openExternal;
    this.wsFactory = wsFactory;
  }

  // ── Auth ──────────────────────────────────────────────────────────────

  /**
   * SDK-orchestrated browser-bounce sign-in (account ADR-021).
   *
   * Opens the cloud login page in the user's system browser with a
   * `?return=<loopback>` query string pointing at the daemon's
   * `/auth/callback` route. The cloud login page handles the sign-in
   * flow (Stytch OTP today; SAML/SSO/OAuth in the future — same code path
   * on the desktop side) and on success redirects to the loopback URL with
   * `?token=<API_JWT>` appended.
   *
   * Resolves after the system browser has been opened — fire-and-forget,
   * matching the tray's `start_browser_signin`. Completion is observed via
   * the daemon's `auth:changed` event stream (`phase: 'usable'`), not the
   * return of this call. Callers that need to await completion can use
   * {@link waitForSignInComplete}.
   *
   * Safe to call again while a sign-in is in flight: the daemon's
   * `POST /auth/begin-signin` reuses the in-flight nonce within the
   * handoff TTL and returns the same `login_url`, so a second click opens
   * another browser tab without invalidating the first.
   *
   * Required SDK config:
   *   - `openExternal(url)` — passed via `SDKConfig.openExternal`. In the
   *     Electron main process this wraps `shell.openExternal`. In the
   *     renderer it wraps an IPC invoke. In Playwright fixtures it points
   *     at a `BrowserContext` so the test can drive the browser side.
   *
   * Options:
   *   - `cloudLoginUrl` — base URL of the cloud login page. If not passed,
   *     callers can read it from `sdk.shogunchop.getEndpoints()` and pass
   *     it in.
   *     cloud login URL. The cloud login page POSTs it to
   *     `/internal/test-tickets/verify` instead of rendering the Stytch
   *     OTP form. Used only by the Track 1 Playwright fixture.
   *
   * Per shogunchop ADR-003 "Security considerations":
   * the daemon binds `/auth/callback` to 127.0.0.1 only; the cloud login
   * page validates `?return=` is a loopback URL before redirecting.
   */
  async signIn(opts?: { cloudLoginUrl?: string }): Promise<void> {
    const openExternal = this.openExternal;
    if (!openExternal) {
      throw new Error(
        'signIn requires an openExternal handler — pass `openExternal` in SDKConfig. ' +
          'In Electron main, use `shell.openExternal`; in renderer, use an IPC bridge.'
      );
    }

    // Daemon generates the handoff nonce and returns the `/desktop-auth`
    // URL. Caller-supplied cloudLoginUrl is a rare
    // override for tests that still must include return+nonce themselves.
    let fullLoginUrl: string;
    if (opts?.cloudLoginUrl) {
      fullLoginUrl = opts.cloudLoginUrl;
    } else {
      const begun = await this.post<{ login_url: string; nonce: string }>('/auth/begin-signin');
      if (!begun?.login_url) {
        throw new Error('auth/begin-signin returned no login_url');
      }
      fullLoginUrl = begun.login_url;
    }

    await openExternal(fullLoginUrl);
  }

  /**
   * Wait for an `auth:changed` frame carrying `phase: 'usable'`.
   *
   * Optional helper for callers that need to block until sign-in completes
   * (e.g. Playwright fixtures). The desktop renderer does not use this —
   * it mirrors the daemon event stream in `DaemonSessionGate`.
   *
   * Rejects with `SignInTimeoutError` after `timeoutMs` (default 5 minutes),
   * and rejects immediately on a frame with no numeric `seq`.
   */
  async waitForSignInComplete(opts?: { timeoutMs?: number }): Promise<void> {
    await this.waitForAuthChanged(opts?.timeoutMs ?? 300_000);
  }

  /**
   * Wait for an `auth:changed` frame carrying `phase: 'usable'`.
   *
   * Rejects with `SignInTimeoutError` after `timeoutMs`, and rejects
   * immediately on a frame with no numeric `seq` — a broken `/events` contract
   * is a loud failure, never a frame we quietly skip.
   *
   * The `seq` guard matters because `ShogunChopEventStream` auto-reconnects: on
   * reconnect the daemon may re-emit its current state, so without a monotonic
   * gate a stale `usable` from before a sign-out could resolve a sign-in that
   * has not happened. Frames at or below the highest `seq` this waiter has seen
   * are duplicates by definition and carry no new information.
   */
  private waitForAuthChanged(timeoutMs: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let highestSeq = -1;
      let stream: ShogunChopEventStream | null = null;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const cleanup = () => {
        if (timer) clearTimeout(timer);
        try {
          stream?.close();
        } catch {
          /* stream already closed — fine */
        }
      };
      timer = setTimeout(() => {
        cleanup();
        const err = new Error(
          `signIn timed out after ${timeoutMs}ms waiting for auth:changed — user likely closed the browser`
        );
        (err as Error & { name: string }).name = 'SignInTimeoutError';
        reject(err);
      }, timeoutMs);
      const hasGlobalWs = Boolean((globalThis as { WebSocket?: unknown }).WebSocket);
      const factory: WebSocketFactory | undefined =
        this.wsFactory ?? (hasGlobalWs ? browserWebSocketFactory : undefined);
      if (!factory) {
        cleanup();
        reject(
          new Error(
            'signIn requires a WebSocket — pass `wsFactory` in the SDKConfig, or run in a context with a global WebSocket'
          )
        );
        return;
      }
      try {
        stream = this.events(factory);
      } catch (err) {
        cleanup();
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      stream.on((event) => {
        if (event.event !== 'auth:changed') return;
        const decision = decideAuthChangedFrame(highestSeq, event);
        if (decision.action === 'ignore') return;
        if (decision.action === 'reject') {
          cleanup();
          reject(new Error(decision.message));
          return;
        }
        if (decision.action === 'accept' && decision.data.phase) {
          // waitForSignInComplete resolves on the first usable frame; the
          // phase rides the widened decision's data.
          if (decision.data.phase === 'usable') {
            cleanup();
            resolve();
          }
          return;
        }
        highestSeq = decision.seq;
        if (decision.action === 'accept' && decision.data.phase === 'usable') {
          cleanup();
          resolve();
        }
      });
    });
  }

  async authStatus(): Promise<ShogunChopAuthStatus> {
    return this.get<ShogunChopAuthStatus>('/auth/status');
  }

  /**
   * The daemon's current phase, in the shape `/events` pushes — the boot
   * snapshot the client is published on (sdk ADR-013).
   * Unlike `authInfo()` it answers for EVERY phase, signed out included, and
   * carries no bearer.
   */
  async authState(): Promise<ShogunChopAuthSnapshot> {
    return this.get<ShogunChopAuthSnapshot>('/auth/state');
  }

  /**
   * Get the full auth payload (JWT + tenant claims) the daemon persisted
   * after the browser-bounce callback. The Electron renderer uses this to
   * hydrate its auth atoms once the daemon has finished sign-in, so the
   * user never sees an in-app OTP form (account ADR-021).
   *
   * Returns `null` when the daemon has no tokens yet (the 401 case) —
   * callers should treat that as "still pre-auth", not an error.
   */
  async authInfo(): Promise<ShogunChopAuthInfo | null> {
    try {
      return await this.get<ShogunChopAuthInfo>('/auth/info');
    } catch (err) {
      // 401 from the daemon means "no tokens yet" — a valid pre-auth state, not
      // an error. Decided on the STATUS NUMBER, never on message text: this
      // used to be `msg.includes('401')`, which is the same
      // guess-from-prose anti-pattern that produced the desktop sign-in loop
      // and which additionally swallows any 5xx whose body mentions "401".
      // Everything else (network, 5xx, malformed JSON) propagates untouched.
      if (err instanceof ShogunChopHTTPError && err.status === 401) {
        return null;
      }
      throw err;
    }
  }

  // `login()` was removed — it called the daemon's `POST /auth/login`
  // route which constructed a URL pointing at a cloud endpoint that never
  // existed. Use `signIn()` for the SDK-orchestrated browser-bounce
  // flow (account ADR-021 class 3).

  async logout(): Promise<void> {
    await this.post<void>('/auth/logout');
  }

  // ── Bridge ────────────────────────────────────────────────────────────

  async bridgeStatus(): Promise<ShogunChopBridgeStatus> {
    return this.get<ShogunChopBridgeStatus>('/bridge/status');
  }

  // ── MCP ───────────────────────────────────────────────────────────────

  async mcpServers(): Promise<ShogunChopMcpServer[]> {
    return this.get<ShogunChopMcpServer[]>('/mcp/servers');
  }

  async addMcpServer(req: AddShogunChopMcpServerRequest): Promise<ShogunChopMcpServer> {
    return this.post<ShogunChopMcpServer>('/mcp/servers', req);
  }

  async removeMcpServer(id: string): Promise<void> {
    await this.del<void>(`/mcp/servers/${id}`);
  }

  async mcpTools(): Promise<ShogunChopMcpTool[]> {
    return this.get<ShogunChopMcpTool[]>('/mcp/tools');
  }

  // ── Indexing ──────────────────────────────────────────────────────────

  async indexStart(req: ShogunChopIndexStartRequest): Promise<void> {
    await this.post<void>('/index/start', req);
  }

  async indexStop(workspaceId: string): Promise<void> {
    await this.post<void>('/index/stop', { workspace_id: workspaceId });
  }

  async indexStatus(workspaceId: string): Promise<ShogunChopIndexStatus> {
    return this.get<ShogunChopIndexStatus>(`/index/status/${workspaceId}`);
  }

  // Local index *search* was removed with qdrant-search: there is no
  // local index/query anymore. File search is served by the central tier via
  // sdk.search.search({ scope: 'files', workspace_id }). The daemon still
  // produces + emits chunks (indexStart/indexStatus) but does not query.

  // ── Local Tools ──────────────────────────────────────────────────────

  async executeLocalTool(
    toolId: string,
    params: Record<string, unknown>
  ): Promise<ShogunChopToolResult> {
    return this.post<ShogunChopToolResult>('/tools/execute', { tool_id: toolId, params });
  }

  // ── Shogun platform_call (desktop arm) ────────────────────────────────

  /**
   * Record the user's confirm-UI approval for a destructive `platform_call`.
   * The daemon holds the authoritative approval store; the agent's re-issued call then executes. Keyed by the server-stamped
   * `idempotency_key` (the ONLY value the renderer can supply — it cannot
   * recompute the sha256).
   */
  async approvePlatformCall(idempotencyKey: string): Promise<void> {
    await this.post('/shogun/approve', { idempotency_key: idempotencyKey });
  }

  /**
   * Relay the renderer executor's outcome for a forwarded `platform_call` back
   * to the awaiting daemon bridge handler, correlated by `request_id`. Exactly
   * one of `result` / `error` is set — an error is surfaced verbatim to the
   * agent (no fabricated success).
   */
  async postPlatformCallResult(
    requestId: string,
    outcome: { result: unknown } | { error: string }
  ): Promise<void> {
    await this.post('/shogun/platform-call-result', { request_id: requestId, ...outcome });
  }

  // ── Events ───────────────────────────────────────────────────────────

  events(wsFactory: WebSocketFactory = browserWebSocketFactory): ShogunChopEventStream {
    const url = this.baseUrl.replace(/^http/, 'ws') + '/events';
    // The credential is fetched PER CONNECT, not captured once: the daemon's JWT
    // is short-lived, and a reconnect after a refresh must present the current
    // one or the handshake is refused (la ADR-013).
    return new ShogunChopEventStream(url, wsFactory, async () => {
      const info = await this.authInfo();
      return info?.token ?? null;
    });
  }

  /** One renderer, one `/events` socket: ref-counted, no `close()` on the handle (la ADR-013, la ADR-015). */
  sharedEvents(wsFactory: WebSocketFactory = browserWebSocketFactory): ShogunChopSharedEvents {
    if (!this.sharedStream) {
      this.sharedStream = this.events(wsFactory);
    }
    const stream = this.sharedStream;
    this.sharedRefs += 1;

    const unsubscribes: Array<() => void> = [];
    let released = false;

    return {
      on: (handler) => {
        const off = stream.on(handler);
        unsubscribes.push(off);
        return off;
      },
      onOpen: (handler) => {
        const off = stream.onOpen(handler);
        unsubscribes.push(off);
        return off;
      },
      onClose: (handler) => {
        const off = stream.onClose(handler);
        unsubscribes.push(off);
        return off;
      },
      registerWorkspaceProjection: (
        workspaceId,
        snapshot,
        subscribe,
        unsubscribe,
        handler,
        onError
      ) => {
        const registration = stream.registerWorkspaceProjection(
          workspaceId,
          snapshot,
          subscribe,
          unsubscribe,
          handler,
          onError
        );
        unsubscribes.push(registration.unsubscribe);
        return registration;
      },
      // Not gated on `released`: a consumer's trailing releases must still reach a socket other handles keep open (shogunchop ADR-ab462).
      send: (frame) => stream.send(frame),
      requestCatalogHold: (op, scopeType, scopeId) =>
        stream.requestCatalogHold(op, scopeType, scopeId),
      release: () => {
        if (released) return;
        released = true;
        for (const off of unsubscribes) off();
        unsubscribes.length = 0;
        this.sharedRefs -= 1;
        if (this.sharedRefs > 0) return;
        stream.close();
        if (this.sharedStream === stream) this.sharedStream = null;
      },
    };
  }

  // ── Info ──────────────────────────────────────────────────────────────

  async info(): Promise<ShogunChopInfo> {
    return this.get<ShogunChopInfo>('/info');
  }

  async health(): Promise<{ status: string }> {
    return this.get<{ status: string }>('/health');
  }

  /**
   * Daemon + bundle versions. `version` is always the running crate version;
   * `bundle_version` is `null` when the daemon runs outside a macOS app
   * bundle (dev mode), and the CFBundleShortVersionString otherwise. The
   * About panel renders both side-by-side.
   */
  async version(): Promise<ShogunChopVersion> {
    return this.get<ShogunChopVersion>('/version');
  }

  /**
   * Wipe ALL local ShogunChop state — tokens, indexes, MCP server registrations,
   * cached config — except `device_id`. Emits an `auth:changed` event so any
   * UI subscribed to events transitions to the signed-out state immediately.
   *
   * The Electron app's Settings → "Reset local state" calls this.
   */
  async resetLocalState(): Promise<void> {
    await this.post('/reset');
  }

  /**
   * Restore the daemon to a known-good authenticated state as its **configured**
   * identity, returning the identity it restored.
   *
   * Exists because the in-cluster pod daemon is a shared mutable resource across
   * two test tiers: the SDK integration tier deliberately exercises `logout()`,
   * `resetLocalState()`, and a handoff sign-in (all real product surfaces), and
   * those mutations outlive the file that made them. Without a restore path, a
   * consumer's result depends on which tier ran last.
   *
   * **Assert the returned identity, not merely `logged_in`.** The failure this
   * exists to catch is a *re-tenanted* daemon — signed in as a different member
   * of the same account — which a bare `logged_in` check passes. That is why the
   * daemon returns its claims rather than just a status.
   *
   * Available only on a daemon built with the `test-auth` cargo feature AND
   * running with `SHOGUNCHOP_AUTH_MODE=test`. The route does not exist in a
   * production build (404) and refuses outside test mode (403) — both are loud,
   * neither is a silent no-op.
   */
  async restoreTestAuth(): Promise<ShogunChopRestoredAuth> {
    return this.post<ShogunChopRestoredAuth>('/test/restore-auth');
  }

  /**
   * Graceful shutdown of the daemon process. The tray's Quit menu item
   * calls this BEFORE terminating the Electron app, so launchd's
   * `KeepAlive { SuccessfulExit = false }` lets the daemon stay quit
   * instead of respawning it.
   */
  async shutdown(): Promise<void> {
    await this.post('/shutdown');
  }

  /**
   * Cloud endpoint configuration the daemon was seeded with (per-stack).
   * The Electron main process calls this once at SDK construction to learn
   * which cloud the daemon is bound to, rather than hardcoding a cloud URL
   * in JavaScript. See shogunchop ADR-001.
   */
  async getEndpoints(): Promise<ShogunChopEndpoints> {
    return this.get<ShogunChopEndpoints>('/config/endpoints');
  }

  // ── Internal HTTP helpers ─────────────────────────────────────────────

  private async get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  private async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  private async del<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type');
    let data: unknown;

    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      throw new ShogunChopHTTPError(response.status, response.statusText, url, data);
    }

    return data as T;
  }
}

// ── Event Stream ─────────────────────────────────────────────────────

export type ShogunChopEventHandler = (event: ShogunChopEvent) => void;

/**
 * The one browser socket factory every renderer subscriber uses.
 *
 * It exists because `protocols` is where the credential rides (la ADR-013), and
 * a hand-written `(url) => new WebSocket(url)` silently drops it — the handshake
 * is then refused the moment the daemon holds a session, and the subscriber
 * looks merely quiet. Six call sites had exactly that shape. Making this the
 * DEFAULT argument of `events()` is what removes the rule someone must remember.
 */
export const browserWebSocketFactory: WebSocketFactory = (url, protocols) => {
  const ctor = (globalThis as { WebSocket?: typeof WebSocket }).WebSocket;
  if (!ctor) {
    throw new Error('no WebSocket implementation in this environment');
  }
  return new ctor(url, protocols) as unknown as WebSocketLike;
};

/** The subprotocol naming this stream; the credential rides beside it. */
export const SHOGUNCHOP_EVENTS_PROTOCOL = 'shogunchop.events.v1';

/**
 * Close codes the daemon uses deliberately. `LAG` is `RecvError::Lagged` — the
 * subscriber fell behind and frames were DROPPED from the broadcast ring
 * (la ADR-010). `REAUTH_REQUIRED` is a connection established without a
 * credential that must re-handshake now the daemon holds one (la ADR-015).
 */
export const SHOGUNCHOP_CLOSE_LAG = 1013;
export const SHOGUNCHOP_CLOSE_REAUTH_REQUIRED = 4001;

/**
 * Why a stream closed, delivered to consumers.
 *
 * Before this the stream reconnected silently, so the daemon's LOUD lag close —
 * which names the skipped-frame count precisely so a consumer can resync —
 * reached nobody, and a renderer could not tell "nothing happened" from "you
 * missed N frames". Reconnection behaviour is unchanged; the point is
 * that it is no longer silent.
 */
export interface ShogunChopStreamClose {
  code: number;
  reason: string;
  /** True when frames were dropped: the consumer should resync, not just wait. */
  lostFrames: boolean;
  willReconnect: boolean;
}

export type ShogunChopCloseHandler = (close: ShogunChopStreamClose) => void;
export type ShogunChopOpenHandler = () => void;

type WorkspaceProjectionRegistration = {
  synchronizer: WorkspaceProjectionSynchronizer;
  onError: (error: unknown) => void;
  cancelPendingOpen?: () => void;
};

type WorkspaceProjectionRegistrationGroup = {
  registrations: Set<WorkspaceProjectionRegistration>;
  pendingConnections: Set<Promise<void>>;
  unsubscribe: () => Promise<void>;
};

export class ShogunChopEventStream {
  private ws: WebSocketLike | null = null;
  private handlers: ShogunChopEventHandler[] = [];
  private readonly url: string;
  private readonly wsFactory: WebSocketFactory;
  private readonly credential: () => Promise<string | null>;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private open = false;
  private openHandlers: ShogunChopOpenHandler[] = [];
  private closeHandlers: ShogunChopCloseHandler[] = [];
  private workspaceProjectionSynchronizers = new Map<
    string,
    WorkspaceProjectionRegistrationGroup
  >();
  private readonly workspaceProjectionReleaseTails = new Map<string, Promise<void>>();
  private readonly pendingHoldResults = new Map<
    string,
    { resolve: () => void; reject: (err: Error) => void }
  >();
  private nextHoldRequestId = 0;

  constructor(
    url: string,
    wsFactory: WebSocketFactory,
    credential: () => Promise<string | null> = () => Promise.resolve(null)
  ) {
    this.url = url;
    this.wsFactory = wsFactory;
    this.credential = credential;
    void this.connect();
  }

  private async connect() {
    if (this.closed) return;
    // A credential fetch that FAILED is not "no credential": the latter is a
    // legitimate credential-less connect (the daemon holds no session), the
    // former means we could not ask. Treat it as a failed attempt and retry on
    // the same cadence as a dropped socket — never as a credential-less
    // connect, and never as an unhandled rejection off the `void connect()`
    // the constructor and the reconnect timer both use.
    let token: string | null;
    try {
      token = await this.credential();
    } catch {
      if (!this.closed) {
        this.reconnectTimer = setTimeout(() => void this.connect(), 3000);
      }
      return;
    }
    if (this.closed) return;
    // A credential-less connect is legitimate ONLY while the daemon holds no
    // session — it is how the tray observes the sign-in transition. The daemon
    // closes such a connection the moment it acquires one, and the reconnect
    // below then presents the new credential.
    const protocols = token ? [SHOGUNCHOP_EVENTS_PROTOCOL, token] : [SHOGUNCHOP_EVENTS_PROTOCOL];
    this.ws = this.wsFactory(this.url, protocols);

    this.ws.onopen = () => {
      if (this.closed) return;
      this.open = true;
      for (const group of this.workspaceProjectionSynchronizers.values()) {
        for (const { synchronizer, onError } of group.registrations) {
          synchronizer.connectionOpened().catch(onError);
        }
      }
      for (const handler of this.openHandlers) handler();
    };

    this.ws.onmessage = (msg) => {
      const raw = JSON.parse(msg.data) as unknown;
      if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
        throw new Error('ShogunChop event frame must be an object');
      }
      const candidate = raw as { event?: unknown; data?: unknown };
      if (candidate.event === 'catalog.hold.result') {
        this.settleHoldResult(candidate.data);
        return;
      }
      if (candidate.event === 'workspace.stateChanged') {
        const projection = parseWorkspaceProjection(
          candidate.data,
          'ShogunChop workspace.stateChanged data'
        );
        const group = this.workspaceProjectionSynchronizers.get(projection.workspace_id);
        if (group) {
          for (const { synchronizer } of group.registrations) synchronizer.accept(projection);
        }
        return;
      }
      if (candidate.event === WORKSPACE_CONTEXT_OBSERVED) {
        const observed: ShogunChopEvent = {
          event: WORKSPACE_CONTEXT_OBSERVED,
          data: parseWorkspaceContextObserved(
            candidate.data,
            `ShogunChop ${WORKSPACE_CONTEXT_OBSERVED} data`
          ),
        };
        for (const handler of this.handlers) handler(observed);
        return;
      }
      const event = raw as ShogunChopEvent;
      for (const handler of this.handlers) {
        handler(event);
      }
    };

    this.ws.onclose = (ev) => {
      if (this.closed) return;
      this.open = false;
      this.rejectPendingHoldResults('the /events socket closed before the daemon replied');
      for (const group of this.workspaceProjectionSynchronizers.values()) {
        for (const { synchronizer } of group.registrations) synchronizer.connectionClosed();
      }
      const code = ev?.code ?? 0;
      const notice: ShogunChopStreamClose = {
        code,
        reason: ev?.reason ?? '',
        lostFrames: code === SHOGUNCHOP_CLOSE_LAG,
        willReconnect: true,
      };
      for (const handler of this.closeHandlers) {
        handler(notice);
      }
      this.reconnectTimer = setTimeout(() => void this.connect(), 3000);
    };
  }

  onOpen(handler: ShogunChopOpenHandler): () => void {
    let subscribed = true;
    this.openHandlers.push(handler);
    if (this.open) {
      queueMicrotask(() => {
        if (subscribed && this.open && !this.closed) handler();
      });
    }
    return () => {
      subscribed = false;
      this.openHandlers = this.openHandlers.filter((h) => h !== handler);
    };
  }

  /**
   * Subscribe to close notices. A consumer that registers none behaves exactly
   * as before — the channel is additive.
   */
  onClose(handler: ShogunChopCloseHandler): () => void {
    this.closeHandlers.push(handler);
    return () => {
      this.closeHandlers = this.closeHandlers.filter((h) => h !== handler);
    };
  }

  send(frame: Record<string, unknown>): void {
    if (!this.open || !this.ws) {
      throw new EventsSocketClosedError('the /events socket is not open');
    }
    this.ws.send(JSON.stringify(frame));
  }

  requestCatalogHold(op: CatalogHoldOp, scopeType: string, scopeId: string): Promise<void> {
    const reqId = String(++this.nextHoldRequestId);
    return new Promise<void>((resolve, reject) => {
      this.pendingHoldResults.set(reqId, { resolve, reject });
      try {
        this.send({ op, scope_type: scopeType, scope_id: scopeId, req_id: reqId });
      } catch (err) {
        this.pendingHoldResults.delete(reqId);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  private settleHoldResult(data: unknown): void {
    if (this.closed) return;
    if (typeof data !== 'object' || data === null) {
      throw new Error('catalog.hold.result frame must carry an object');
    }
    const { req_id: reqId, ok, error } = data as { req_id?: unknown; ok?: unknown; error?: unknown };
    if (typeof reqId !== 'string' || typeof ok !== 'boolean') {
      throw new Error('catalog.hold.result frame must carry a string req_id and a boolean ok');
    }
    const pending = this.pendingHoldResults.get(reqId);
    if (!pending) {
      throw new Error(`catalog.hold.result for unknown req_id ${reqId}`);
    }
    this.pendingHoldResults.delete(reqId);
    if (ok) {
      pending.resolve();
      return;
    }
    pending.reject(new Error(`daemon refused the catalog request: ${String(error)}`));
  }

  private rejectPendingHoldResults(reason: string): void {
    const pending = [...this.pendingHoldResults.values()];
    this.pendingHoldResults.clear();
    for (const { reject } of pending) reject(new EventsSocketClosedError(reason));
  }

  on(handler: ShogunChopEventHandler): () => void {
    this.handlers.push(handler);
    return () => {
      this.handlers = this.handlers.filter((h) => h !== handler);
    };
  }

  registerWorkspaceProjection(
    workspaceId: string,
    snapshot: () => Promise<WorkspaceProjection>,
    subscribe: (afterRevision: number) => Promise<void>,
    unsubscribe: () => Promise<void>,
    handler: WorkspaceProjectionHandler,
    onError: (error: unknown) => void
  ): { registered: Promise<void>; unsubscribe: () => void } {
    const synchronizedSubscribe = async (afterRevision: number): Promise<void> => {
      const pendingRelease = this.workspaceProjectionReleaseTails.get(workspaceId);
      if (pendingRelease) await pendingRelease;
      await subscribe(afterRevision);
    };
    const synchronizer = new WorkspaceProjectionSynchronizer(
      snapshot,
      synchronizedSubscribe,
      handler
    );
    const registration: WorkspaceProjectionRegistration = { synchronizer, onError };
    const group: WorkspaceProjectionRegistrationGroup = this.workspaceProjectionSynchronizers.get(
      workspaceId
    ) ?? {
      registrations: new Set<WorkspaceProjectionRegistration>(),
      pendingConnections: new Set<Promise<void>>(),
      unsubscribe,
    };
    group.registrations.add(registration);
    this.workspaceProjectionSynchronizers.set(workspaceId, group);
    const registered = this.open
      ? synchronizer.connectionOpened()
      : new Promise<void>((resolve, reject) => {
          const off = this.onOpen(() => {
            off();
            registration.cancelPendingOpen = undefined;
            synchronizer.connectionOpened().then(resolve, reject);
          });
          registration.cancelPendingOpen = () => {
            off();
            registration.cancelPendingOpen = undefined;
            resolve();
          };
        });
    registered.catch(() => {});
    return {
      registered,
      unsubscribe: () => {
        if (!group.registrations.has(registration)) return;
        registration.cancelPendingOpen?.();
        const pendingConnection = synchronizer.currentConnection().catch(() => {});
        synchronizer.connectionClosed();
        group.registrations.delete(registration);
        group.pendingConnections.add(pendingConnection);
        void pendingConnection.finally(() => group.pendingConnections.delete(pendingConnection));
        if (group.registrations.size > 0) return;
        this.releaseWorkspaceProjectionGroup(workspaceId, group, onError);
      },
    };
  }

  private releaseWorkspaceProjectionGroup(
    workspaceId: string,
    group: WorkspaceProjectionRegistrationGroup,
    onError: (error: unknown) => void
  ): void {
    this.workspaceProjectionSynchronizers.delete(workspaceId);
    const previousRelease = this.workspaceProjectionReleaseTails.get(workspaceId);
    const release = Promise.all([
      ...(previousRelease ? [previousRelease] : []),
      ...group.pendingConnections,
    ]).then(() => group.unsubscribe());
    this.workspaceProjectionReleaseTails.set(workspaceId, release);
    const clearRelease = (): void => {
      if (this.workspaceProjectionReleaseTails.get(workspaceId) === release) {
        this.workspaceProjectionReleaseTails.delete(workspaceId);
      }
    };
    void release.then(clearRelease, (error) => {
      clearRelease();
      onError(error);
    });
  }

  close() {
    this.closed = true;
    this.open = false;
    this.rejectPendingHoldResults('the /events stream was closed before the daemon replied');
    for (const [workspaceId, group] of this.workspaceProjectionSynchronizers) {
      const [registration] = group.registrations;
      for (const current of group.registrations) {
        current.cancelPendingOpen?.();
        const pendingConnection = current.synchronizer.currentConnection().catch(() => {});
        current.synchronizer.connectionClosed();
        group.pendingConnections.add(pendingConnection);
      }
      group.registrations.clear();
      if (registration) {
        this.releaseWorkspaceProjectionGroup(workspaceId, group, registration.onError);
      }
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }
}
